import { useEffect, useState } from 'react'
import { useQuery, useMutation, useQueryClient, useInfiniteQuery } from '@tanstack/react-query'
import {
  getHourRegistrations,
  createHourRegistration,
  updateHourRegistration,
  deleteHourRegistration,
  getRetainers,
  createRetainer,
  updateRetainer,
  deleteRetainer,
  clockIn,
  clockOut,
  getClockStatus,
} from '../api/react/hourregistration/hourRegistrations'
import type { ClockInOutPayload, HourRegistrationExpanded, HourRegistrationQueryParams, ManualHourRegistrationPayload, RetainerPayload, RetainerQueryParams } from '../types/hourRegistrations.types'

// ── Query Keys ───────────────────────────────────

export const HOUR_REGISTRATION_KEYS = {
  list: (params: HourRegistrationQueryParams) => ['hourRegistrations', params] as const,
  all: () => ['hourRegistrations'] as const,
  clockStatus: (userId: string) => ['clockStatus', userId] as const,
}

export const RETAINER_KEYS = {
  list: (params: RetainerQueryParams) => ['retainers', params] as const,
  all: () => ['retainers'] as const,
}

// ── Hour Registration Queries ────────────────────

export const useGetHourRegistrations = (params: HourRegistrationQueryParams) => {
  return useQuery({
    queryKey: HOUR_REGISTRATION_KEYS.list(params),
    queryFn: () => getHourRegistrations(params),
  })
}

export const useInfiniteHourRegistrations = (
  params: Omit<HourRegistrationQueryParams, 'page'>,
  enabled = true,
) => {
  return useInfiniteQuery({
    queryKey: ['hourRegistrations', 'infinite', params] as const,
    queryFn: ({ pageParam }: { pageParam: number }) =>
      getHourRegistrations({ ...params, page: pageParam }),
    initialPageParam: 1,
    getNextPageParam: (lastPage) => lastPage.nextPage ?? undefined,
    enabled,
  })
}

export const useGetClockStatus = (userId: string) => {
  return useQuery({
    queryKey: HOUR_REGISTRATION_KEYS.clockStatus(userId),
    queryFn: () => getClockStatus(userId),
    enabled: !!userId,
  })
}

// ── Hour Registration Mutations ──────────────────

export const useClockIn = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: ClockInOutPayload) => clockIn(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: HOUR_REGISTRATION_KEYS.all() })
    },
  })
}

export const useClockOut = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: ClockInOutPayload) => clockOut(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: HOUR_REGISTRATION_KEYS.all() })
    },
  })
}

export const useCreateHourRegistration = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: ManualHourRegistrationPayload) => createHourRegistration(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: HOUR_REGISTRATION_KEYS.all() })
    },
  })
}

export const useUpdateHourRegistration = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, data }: { id: string | number; data: Partial<ManualHourRegistrationPayload> }) =>
      updateHourRegistration(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: HOUR_REGISTRATION_KEYS.all() })
    },
  })
}

export const useDeleteHourRegistration = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string | number) => deleteHourRegistration(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: HOUR_REGISTRATION_KEYS.all() })
    },
  })
}

// ── Retainer Queries ─────────────────────────────

export const useGetRetainers = (params: RetainerQueryParams) => {
  return useQuery({
    queryKey: RETAINER_KEYS.list(params),
    queryFn: () => getRetainers(params),
  })
}

// ── Retainer Mutations ───────────────────────────

export const useCreateRetainer = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: RetainerPayload) => createRetainer(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: RETAINER_KEYS.all() })
    },
  })
}

export const useUpdateRetainer = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, data }: { id: string | number; data: Partial<RetainerPayload> }) =>
      updateRetainer(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: RETAINER_KEYS.all() })
    },
  })
}

export const useDeleteRetainer = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string | number) => deleteRetainer(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: RETAINER_KEYS.all() })
    },
  })
}

// ── Fetch All (for client-side sorting) ─────────

export const useFetchAllHourRegistrations = (
  params: HourRegistrationQueryParams,
  enabled: boolean,
) => {
  const [data, setData] = useState<{ rows: HourRegistrationExpanded[]; count: number } | null>(null)
  const [isFetching, setIsFetching] = useState(false)

  useEffect(() => {
    if (!enabled) {
      setData(null)
      return
    }

    let cancelled = false
    setIsFetching(true)

    const fetchAll = async () => {
      try {
        const PAGE_SIZE = 500
        const first = await getHourRegistrations({ ...params, page: 1, page_size: PAGE_SIZE })
        if (cancelled) return

        const total = first.count ?? first.data.length
        const totalPages = Math.ceil(total / PAGE_SIZE)

        const remainingPages = Array.from({ length: totalPages - 1 }, (_, i) => i + 2)
        const remainingData = await Promise.all(
          remainingPages.map((p) => getHourRegistrations({ ...params, page: p, page_size: PAGE_SIZE })),
        )

        if (cancelled) return

        const allData = [first, ...remainingData].flatMap((res) => res.data || [])
        setData({ rows: allData, count: total })
      } catch (err) {
        if (!cancelled) {
          console.error('Error fetching all hour registrations:', err)
          setData(null)
        }
      } finally {
        if (!cancelled) setIsFetching(false)
      }
    }

    fetchAll()
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, JSON.stringify(params, Object.keys(params).sort())])

  return { data, isFetching }
}
